import React from "react";
import { Box, Typography, Grid } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { isAnswerCorrect } from "../helpers/answerCorrectness";
import AnswerField from "./AnswerField";
import CheckboxGroupField from "./CheckboxGroupField";
import RadioButtonGroupField from "./RadioButtonFieldGroup";

// Map question types to shared components for editing mode.
// You can extend this mapping as needed.
const componentMap = {
  short_answer: AnswerField,
  long_answer: AnswerField,
  checkbox_group: CheckboxGroupField,
  custom_checkbox_group: CheckboxGroupField,
  radio_button_group: RadioButtonGroupField,
};

const DynamicQuestionGroupField = ({
  module,
  responses,
  onChange,
  isEditable = false,
}) => {
  // If in editing mode, render each question with its input field.
  if (isEditable && typeof onChange === "function") {
    return (
      <Box marginY={2}>
        <Typography variant="h6" gutterBottom>
          {module.label}
        </Typography>
        {module.questions.map((question) => {
          const QuestionComponent = componentMap[question.type] || AnswerField;
          return (
            <QuestionComponent
              key={question.id}
              module={question}
              value={responses[question.id]}
              isEditable={true}
              fieldKey={question.id}
              onChange={onChange}
              type={question.type}
            />
          );
        })}
      </Box>
    );
  }

  // Otherwise, render the view-only version with answer correctness.
  const dynamicQuestions = module.questions.filter(
    (question) => responses[question.id],
  );

  if (dynamicQuestions.length === 0) return null;

  return (
    <Box marginY={2}>
      <Typography variant="h6" gutterBottom>
        {module.label}
      </Typography>
      {dynamicQuestions.map((question) => {
        const userAnswer = responses[question.id];
        const correctResponses = question.correctResponses || [];
        const isCorrect = isAnswerCorrect(
          userAnswer,
          correctResponses,
          question.type,
        );
        const incorrectAnswers = Array.isArray(userAnswer)
          ? userAnswer.filter((ans) => !correctResponses.includes(ans))
          : !correctResponses.includes(userAnswer)
            ? [userAnswer]
            : [];

        return (
          <Box
            key={question.id}
            marginY={1}
            padding={2}
            border={1}
            borderColor="grey.300"
            borderRadius={2}
          >
            <Grid container alignItems="center" spacing={2}>
              <Grid item xs={12} sm={8}>
                <Typography variant="subtitle1">{question.label}</Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                {question.type === "checkbox_group" ||
                question.type === "custom_checkbox_group" ? (
                  <Box>
                    {userAnswer.length > 0 ? (
                      userAnswer.map((ans, idx) => (
                        <Typography key={idx} variant="body2">
                          {ans}
                        </Typography>
                      ))
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        No answer provided
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <Typography variant="body2">
                    {userAnswer || "No answer provided"}
                  </Typography>
                )}
              </Grid>
            </Grid>
            {correctResponses.length > 0 && (
              <Box display="flex" alignItems="center" marginTop={1}>
                {isCorrect ? (
                  <CheckCircleIcon color="success" sx={{ marginRight: 1 }} />
                ) : (
                  <CancelIcon color="error" sx={{ marginRight: 1 }} />
                )}
                <Typography variant="body2">
                  {isCorrect ? "Correct" : "Incorrect"}
                </Typography>
              </Box>
            )}
            {!isCorrect && incorrectAnswers.length > 0 && (
              <Box marginTop={1} paddingLeft={2}>
                <Typography variant="body2" color="error">
                  Incorrect Answer(s):
                </Typography>
                <ul>
                  {incorrectAnswers.map((ans, idx) => (
                    <li key={idx}>{ans}</li>
                  ))}
                </ul>
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export default DynamicQuestionGroupField;
